import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';

import { PageHeader } from '@/shared/PageHeader';
import { ReportModal } from '@/shared/ReportModal';
import { useReportCounts } from '@/shared/reportCounts';
import { useAuth } from '@/shared/AuthContext';
import { VehicleLoadingCheckModal } from './VehicleLoadingCheckModal';
import { VehicleExportModal } from './VehicleExportModal';
import type { Vehicle, Compartment, CompartmentItem, LoadingCheck } from './types';

// Solo ruolo Magazzino+ puo' aggiungere/togliere/riordinare struttura mezzi/vani/oggetti.
// Tutti gli altri utenti possono usare: controllo caricamento, segnalazioni, export TXT.
function useCanManageVehicles(): boolean {
  const { hasRole } = useAuth();
  return hasRole('Magazzino', 'Amministratore', 'SuperAdmin');
}

export function VehiclesPage(): React.JSX.Element {
  usePageTitle('Gestione Mezzi');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const canManage = useCanManageVehicles();

  const { data: vehicles } = useQuery<Vehicle[]>({
    queryKey: ['vehicles'],
    queryFn: async () => (await api.get('/vehicles')).data,
  });

  return (
    <div className="page-container">
      <PageHeader title="🚒 Gestione Mezzi"
        actions={(
          <>
            {canManage && (
              <button onClick={() => setShowAddVehicle(true)}
                style={{ padding: '10px 18px', borderRadius: 8, fontWeight: 700, border: 'none', cursor: 'pointer',
                         background: 'linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)', color: 'white',
                         boxShadow: '0 2px 6px rgba(253, 126, 20, 0.35)' }}>
                + Aggiungi Mezzo
              </button>
            )}
            <button onClick={() => setShowExport(true)}
              style={{ padding: '10px 18px', borderRadius: 8, fontWeight: 700, border: 'none', cursor: 'pointer',
                       background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white',
                       boxShadow: '0 2px 6px rgba(40, 167, 69, 0.35)' }}>
              📄 Esporta TXT
            </button>
          </>
        )} />

      {selectedVehicleId ? (
        <VehicleDetail vehicleId={selectedVehicleId} onBack={() => setSelectedVehicleId(null)} />
      ) : (
        <>
          {(vehicles ?? []).length === 0 && (
            <div style={{ textAlign: 'center', color: '#6c757d', padding: 40 }}>
              Nessun mezzo registrato
            </div>
          )}
          <div className="vehicle-card-grid">
            {(vehicles ?? []).map(v => (
              <VehicleCard key={v.id} v={v} onOpen={() => setSelectedVehicleId(v.id)} />
            ))}
          </div>
        </>
      )}

      {showAddVehicle && <VehicleFormModal onClose={() => setShowAddVehicle(false)} />}
      {showExport && <VehicleExportModal vehicles={vehicles ?? []} onClose={() => setShowExport(false)} />}
    </div>
  );
}

function VehicleCard({ v, onOpen }: { v: Vehicle; onOpen: () => void }): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { data: compartments } = useQuery<Compartment[]>({
    queryKey: ['vehicle.compartments', v.id],
    queryFn: async () => (await api.get(`/vehicles/${v.id}/compartments`)).data,
  });
  const vanCount = (compartments ?? []).length;
  const itemCount = (compartments ?? []).reduce((s, c) => s + (c.items?.length ?? 0), 0);

  async function handleDelete(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Elimina mezzo',
      message: `Eliminare "${v.name}"? Verranno eliminati anche tutti i vani e oggetti associati.`,
      confirmLabel: '🗑️ Elimina',
      variant: 'danger',
      icon: '🗑️',
    });
    if (!ok) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      toast.show('Mezzo eliminato', 'success');
    } catch {
      toast.show('Errore eliminazione', 'error');
    }
  }

  const canManage = useCanManageVehicles();
  return (
    <div onClick={onOpen} className="vehicle-card">
      <div className="vehicle-card-header">
        <div className="vehicle-card-name">🚒 {v.name}</div>
        {canManage && (
          <button onClick={handleDelete} title="Elimina mezzo"
            style={{ width: 32, height: 32, padding: 0, border: 'none', borderRadius: 6, cursor: 'pointer',
                     background: '#dc3545', color: 'white', fontSize: 14,
                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            🗑️
          </button>
        )}
      </div>
      {v.plate && <div className="vehicle-card-plate">🪪 Targa: {v.plate}</div>}
      <div className="vehicle-card-meta">
        <span>📦 {vanCount} {vanCount === 1 ? 'vano' : 'vani'}</span>
        <span>🔧 {itemCount} {itemCount === 1 ? 'oggetto' : 'oggetti'}</span>
      </div>
    </div>
  );
}

function VehicleDetail({ vehicleId, onBack }: { vehicleId: string; onBack: () => void }): React.JSX.Element {
  const qc = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);
  const [addingCompartmentParent, setAddingCompartmentParent] = useState<string | null | 'root'>(null);
  const [editingCompartment, setEditingCompartment] = useState<Compartment | null>(null);
  const [addingItemTo, setAddingItemTo] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<CompartmentItem | null>(null);
  const [showLoadingCheck, setShowLoadingCheck] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ vehicleId?: string; compartmentId?: string; itemId?: string; label: string } | null>(null);
  const [reorderMode, setReorderMode] = useState(false);

  // Permessi (richiesta Bryan): solo Magazzino+ aggiunge/toglie/riordina struttura
  // (vani, sotto-vani, oggetti). Tutti possono: segnalazioni, controllo caricamento, export.
  const canManage = useCanManageVehicles();
  const [searchTerm, setSearchTerm] = useState('');

  // Listener per refresh dopo riordino di sotto-vani (event fired da onMoveSub helper)
  useEffect(() => {
    const handler = () => qc.invalidateQueries({ queryKey: ['vehicle.compartments', vehicleId] });
    window.addEventListener('vvf-compartments-reordered', handler);
    return () => window.removeEventListener('vvf-compartments-reordered', handler);
  }, [qc, vehicleId]);

  const { data: vehicle } = useQuery<Vehicle>({
    queryKey: ['vehicle', vehicleId],
    queryFn: async () => (await api.get('/vehicles')).data.find((x: Vehicle) => x.id === vehicleId),
  });

  const { data: compartments } = useQuery<Compartment[]>({
    queryKey: ['vehicle.compartments', vehicleId],
    queryFn: async () => (await api.get(`/vehicles/${vehicleId}/compartments`)).data,
  });

  const { data: checks } = useQuery<LoadingCheck[]>({
    queryKey: ['vehicle.checks', vehicleId],
    queryFn: async () => (await api.get(`/vehicles/${vehicleId}/loading-checks`)).data,
  });

  if (!vehicle) return <div>Caricamento...</div>;

  const lastCheck = checks?.[0];
  const byParent: Record<string, Compartment[]> = {};
  (compartments ?? []).forEach(c => {
    const key = c.parentCompartmentId ?? 'root';
    byParent[key] ??= [];
    byParent[key].push(c);
  });
  // Ordina items per sortOrder dentro ogni compartment
  Object.values(byParent).forEach(arr => arr.sort((a, b) => a.sortOrder - b.sortOrder));

  // Indice ricerca oggetti: ogni oggetto -> path "Vano > Sotto-vano".
  // Oggetti con stesso nome in vani diversi compaiono come voci distinte (richiesta Bryan).
  const compById = new Map((compartments ?? []).map(c => [c.id, c]));
  function pathFor(compartmentId: string): string {
    const chain: string[] = [];
    let cur: Compartment | undefined = compById.get(compartmentId);
    while (cur) {
      chain.unshift(cur.name);
      cur = cur.parentCompartmentId ? compById.get(cur.parentCompartmentId) : undefined;
    }
    return chain.join(' › ');
  }
  const searchResults = searchTerm.trim().length >= 2
    ? (compartments ?? []).flatMap(c =>
        (c.items ?? []).filter(it => it.name.toLowerCase().includes(searchTerm.toLowerCase()))
          .map(it => ({ item: it, path: pathFor(c.id) })))
    : [];

  async function moveCompartment(parentKey: string, index: number, delta: number): Promise<void> {
    const siblings = (byParent[parentKey] ?? []).slice();
    const newIndex = index + delta;
    if (newIndex < 0 || newIndex >= siblings.length) return;
    [siblings[index], siblings[newIndex]] = [siblings[newIndex], siblings[index]];
    await api.post('/vehicles/compartments/reorder', { orderedIds: siblings.map(s => s.id) });
    qc.invalidateQueries({ queryKey: ['vehicle.compartments', vehicleId] });
  }

  async function moveItem(compartmentId: string, index: number, delta: number): Promise<void> {
    const comp = (compartments ?? []).find(c => c.id === compartmentId);
    if (!comp) return;
    const items = (comp.items ?? []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const newIndex = index + delta;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    await api.post('/vehicles/items/reorder', { orderedIds: items.map(i => i.id) });
    qc.invalidateQueries({ queryKey: ['vehicle.compartments', vehicleId] });
  }

  function fmtDate(d?: string): string {
    if (!d) return '—';
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
  }

  return (
    <div>
      {/* Bottoni header */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <button onClick={onBack} className="btn-back">← Torna ai Mezzi</button>
        <button onClick={() => setShowLoadingCheck(true)}
          style={{ padding: '10px 18px', borderRadius: 8, fontWeight: 700, border: 'none', cursor: 'pointer',
                   background: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)', color: '#000',
                   boxShadow: '0 2px 6px rgba(255, 193, 7, 0.4)' }}>
          📋 Controllo Caricamento
        </button>
        {canManage && (
          <button onClick={() => setReorderMode(m => !m)}
            style={{ padding: '10px 18px', borderRadius: 8, fontWeight: 700, border: 'none', cursor: 'pointer',
                     background: reorderMode
                       ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)'
                       : 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                     color: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
            {reorderMode ? '✅ Fine riordino' : '↕️ Riordina vani / oggetti'}
          </button>
        )}
      </div>

      {reorderMode && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffeaa7', padding: 10, borderRadius: 6,
                      marginBottom: 14, fontSize: 13, color: '#856404' }}>
          ℹ️ Modalita' riordino attiva: usa le frecce ▲/▼ accanto a vani/sotto-vani/oggetti per cambiarne l'ordine.
          L'ordine e' globale e visibile a tutti gli utenti.
        </div>
      )}

      {/* Ultimo controllo */}
      {lastCheck && (
        <div className="vehicle-checks-box" style={{ padding: 12,
                      borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          <strong>Ultimo controllo:</strong> {new Date(lastCheck.performedAt).toLocaleString('it-IT')}
          {lastCheck.performedByName && (
            <span style={{ color: '#666' }}> — eseguito da <strong>{lastCheck.performedByName}</strong></span>
          )}
          {checks && checks.length > 1 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: '#0056b3' }}>Storico ({checks.length})</summary>
              <ul style={{ marginTop: 6, paddingLeft: 20, fontSize: 13 }}>
                {checks.slice(1).map(c => (
                  <li key={c.id}>
                    📅 {new Date(c.performedAt).toLocaleString('it-IT')}
                    {c.performedByName && ` — ${c.performedByName}`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Nome mezzo */}
      <h2 style={{ margin: '20px 0' }}>🚒 {vehicle.name}</h2>

      {/* Info Veicolo */}
      <div className="vehicle-info-box" style={{ padding: 18, borderRadius: 10, marginBottom: 20, borderLeft: '4px solid #fd7e14' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, fontSize: 14 }}>
          <div><strong>Targa:</strong> {vehicle.plate || '—'}</div>
          <div><strong>Tagliando:</strong> <span style={{ color: expiryColor(vehicle.inspectionDate) }}>{fmtDate(vehicle.inspectionDate)}</span></div>
          <div><strong>Assicurazione:</strong> <span style={{ color: expiryColor(vehicle.insuranceExpiry) }}>{fmtDate(vehicle.insuranceExpiry)}</span></div>
          <div><strong>Ultima manutenzione:</strong> {fmtDate(vehicle.lastMaintenance)}</div>
        </div>
        {vehicle.maintenanceNotes && (
          <div style={{ marginTop: 12, fontSize: 13, color: '#555', whiteSpace: 'pre-wrap' }}>
            <strong>Note manutenzione:</strong> {vehicle.maintenanceNotes}
          </div>
        )}
        {canManage && (
          <button onClick={() => setShowEdit(true)} style={{ marginTop: 12, padding: '8px 16px',
            border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
            background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)', color: 'white',
            boxShadow: '0 2px 4px rgba(0,123,255,0.3)' }}>
            ✏️ Modifica Informazioni
          </button>
        )}
      </div>

      {/* Ricerca oggetto: digita per trovare dove si trova un oggetto */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: '#555', display: 'block', marginBottom: 6 }}>
          🔍 Cerca oggetto (es. "estintore", "ascia")
        </label>
        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          placeholder="Digita almeno 2 caratteri…"
          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #ced4da',
                   fontSize: 14, color: '#333', background: 'white', colorScheme: 'light' }} />
        {searchTerm.trim().length >= 2 && (
          <div style={{ marginTop: 10, background: '#f8f9fa', border: '1px solid #e9ecef',
                        borderRadius: 8, padding: 10, fontSize: 13 }}>
            {searchResults.length === 0 ? (
              <span style={{ color: '#888' }}>Nessun oggetto trovato in questo mezzo.</span>
            ) : (
              <>
                <strong style={{ color: '#555' }}>{searchResults.length} risultat{searchResults.length === 1 ? 'o' : 'i'}:</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {searchResults.map((r, i) => (
                    <li key={`${r.item.id}-${i}`} style={{ marginBottom: 4, color: '#333' }}>
                      🔧 <strong>{r.item.name}</strong>{' '}
                      <span style={{ color: '#666' }}>({r.item.quantity} {r.item.unit || 'pz'})</span>
                      {' → '}
                      <span style={{ color: '#0056b3', fontWeight: 600 }}>📁 {r.path}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      {/* Bottone aggiungi vano (solo Magazzino+) */}
      {canManage && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <button onClick={() => setAddingCompartmentParent('root')}
            style={{ padding: '10px 16px', borderRadius: 8, fontWeight: 700, border: 'none', cursor: 'pointer',
                     background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white',
                     boxShadow: '0 2px 6px rgba(40, 167, 69, 0.35)' }}>
            + Aggiungi Vano/Cassetta
          </button>
        </div>
      )}

      {/* Lista vani */}
      <h3 style={{ marginBottom: 12 }}>Vani e Oggetti</h3>
      {(byParent['root'] ?? []).length === 0 && (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: 30, background: '#f8f9fa', borderRadius: 8 }}>
          Nessun vano configurato per questo mezzo
        </div>
      )}
      {(byParent['root'] ?? []).map((c, idx, arr) => (
        <CompartmentNode key={c.id} comp={c} byParent={byParent} depth={0}
          reorderMode={reorderMode}
          canManage={canManage}
          siblingIndex={idx}
          siblingsCount={arr.length}
          onMoveComp={(delta) => moveCompartment('root', idx, delta)}
          onMoveItem={moveItem}
          onAddSub={(parentId) => setAddingCompartmentParent(parentId)}
          onAddItem={(compId) => setAddingItemTo(compId)}
          onEditComp={(comp) => setEditingCompartment(comp)}
          onEditItem={(item) => setEditingItem(item)}
          onReport={(target) => setReportTarget(target)} />
      ))}

      {/* Modali */}
      {showEdit && <VehicleFormModal vehicle={vehicle} onClose={() => setShowEdit(false)} />}
      {addingCompartmentParent !== null && (
        <CompartmentFormModal vehicleId={vehicleId}
          parentCompartmentId={addingCompartmentParent === 'root' ? null : addingCompartmentParent}
          onClose={() => setAddingCompartmentParent(null)} />
      )}
      {editingCompartment && (
        <CompartmentFormModal vehicleId={vehicleId} compartment={editingCompartment}
          onClose={() => setEditingCompartment(null)} />
      )}
      {addingItemTo && (
        <ItemFormModal compartmentId={addingItemTo} onClose={() => setAddingItemTo(null)} />
      )}
      {editingItem && (
        <ItemFormModal compartmentId={editingItem.compartmentId} item={editingItem}
          onClose={() => setEditingItem(null)} />
      )}
      {showLoadingCheck && (
        <VehicleLoadingCheckModal vehicleId={vehicleId} compartments={compartments ?? []}
          onClose={() => setShowLoadingCheck(false)} />
      )}
      {reportTarget && (
        <ReportModal target={{
          vehicleId: reportTarget.vehicleId,
          compartmentId: reportTarget.compartmentId,
          itemId: reportTarget.itemId,
        }} targetLabel={reportTarget.label}
          onClose={() => setReportTarget(null)} />
      )}
    </div>
  );
}

function expiryColor(date?: string): string {
  if (!date) return '#666';
  const days = Math.round((new Date(date).getTime() - Date.now()) / 86400000);
  if (days < 0) return '#dc3545';
  if (days < 30) return '#ffc107';
  return '#28a745';
}

interface NodeProps {
  comp: Compartment;
  byParent: Record<string, Compartment[]>;
  depth: number;
  reorderMode?: boolean;
  canManage: boolean;
  siblingIndex?: number;
  siblingsCount?: number;
  onMoveComp?: (delta: number) => void;
  onMoveItem?: (compartmentId: string, index: number, delta: number) => void;
  onAddSub: (parentId: string) => void;
  onAddItem: (compId: string) => void;
  onEditComp: (comp: Compartment) => void;
  onEditItem: (item: CompartmentItem) => void;
  onReport: (target: { compartmentId?: string; itemId?: string; label: string }) => void;
}

async function onMoveSub(siblings: Compartment[], idx: number, delta: number): Promise<void> {
  const arr = siblings.slice();
  const newIndex = idx + delta;
  if (newIndex < 0 || newIndex >= arr.length) return;
  [arr[idx], arr[newIndex]] = [arr[newIndex], arr[idx]];
  await api.post('/vehicles/compartments/reorder', { orderedIds: arr.map(s => s.id) });
  // Trigger refresh dell'albero compartments via QueryClient global
  window.dispatchEvent(new CustomEvent('vvf-compartments-reordered'));
}

function CompartmentNode({ comp, byParent, depth, reorderMode, canManage, siblingIndex, siblingsCount, onMoveComp, onMoveItem, onAddSub, onAddItem, onEditComp, onEditItem, onReport }: NodeProps): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const children = byParent[comp.id] ?? [];
  const [expanded, setExpanded] = useState(true);
  const reportCounts = useReportCounts();
  const compReports = reportCounts.compartment.get(comp.id) ?? 0;

  async function handleDelete(): Promise<void> {
    const ok = await confirm({
      title: depth === 0 ? 'Elimina vano' : 'Elimina sotto-vano',
      message: `Eliminare "${comp.name}"? Verranno eliminati anche tutti gli oggetti e sotto-vani.`,
      confirmLabel: '🗑️ Elimina',
      variant: 'danger',
      icon: '🗑️',
    });
    if (!ok) return;
    try {
      await api.delete(`/vehicles/compartments/${comp.id}`);
      qc.invalidateQueries({ queryKey: ['vehicle.compartments'] });
      toast.show('Eliminato', 'success');
    } catch {
      toast.show('Errore eliminazione', 'error');
    }
  }

  return (
    // Bryan: sottovani allineati verticalmente ai vani (no rientro orizzontale).
    // Il marker visuale dell'indentazione resta il borderLeft colorato + icona.
    <div className={depth === 0 ? 'compartment-card' : 'subcompartment-card'} style={{
      marginBottom: 12,
      border: '1px solid #e9ecef', borderRadius: 8, overflow: 'hidden',
      borderLeft: `4px solid ${depth === 0 ? '#fd7e14' : '#17a2b8'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: 12, gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setExpanded(!expanded)}
          style={{ background: '#343a40', color: '#fff', border: 'none', cursor: 'pointer',
                   fontSize: 12, width: 24, height: 24, borderRadius: 4,
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {expanded ? '▼' : '▶'}
        </button>
        {reorderMode && onMoveComp && typeof siblingIndex === 'number' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button onClick={() => onMoveComp(-1)} disabled={siblingIndex === 0}
              title="Sposta su"
              style={{ background: '#17a2b8', color: 'white', border: 'none', borderRadius: 3,
                       width: 22, height: 16, cursor: siblingIndex === 0 ? 'not-allowed' : 'pointer',
                       fontSize: 9, opacity: siblingIndex === 0 ? 0.4 : 1 }}>▲</button>
            <button onClick={() => onMoveComp(1)} disabled={siblingIndex === (siblingsCount ?? 0) - 1}
              title="Sposta giu"
              style={{ background: '#17a2b8', color: 'white', border: 'none', borderRadius: 3,
                       width: 22, height: 16, cursor: siblingIndex === (siblingsCount ?? 0) - 1 ? 'not-allowed' : 'pointer',
                       fontSize: 9, opacity: siblingIndex === (siblingsCount ?? 0) - 1 ? 0.4 : 1 }}>▼</button>
          </div>
        )}
        <strong style={{ flex: 1 }}>📁 {comp.name}</strong>
        <span style={{ fontSize: 12, color: '#888' }}>
          {children.length > 0 && `${children.length} sotto-vani · `}
          {(comp.items?.length ?? 0)} oggetti
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {canManage && (
            <button onClick={() => onAddItem(comp.id)} title="Aggiungi oggetto"
              style={iconBtn('#28a745')}>➕</button>
          )}
          {canManage && depth === 0 && (
            <button onClick={() => onAddSub(comp.id)} title="Aggiungi sotto-vano"
              style={iconBtn('#17a2b8')}>📂</button>
          )}
          {canManage && (
            <button onClick={() => onEditComp(comp)} title="Modifica"
              style={iconBtn('#007bff')}>✎</button>
          )}
          <button onClick={() => onReport({ compartmentId: comp.id, label: comp.name })} title="Segnalazione"
            style={{ ...iconBtn('#ffc107', '#000'), position: 'relative' }}>
            🚨{compReports > 0 && <span className="report-counter-badge">{compReports}</span>}
          </button>
          {canManage && (
            <button onClick={handleDelete} title="Elimina"
              style={iconBtn('#dc3545')}>🗑️</button>
          )}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 12px 12px' }}>
          {(comp.items ?? []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map((it, idx, arr) => (
            <ItemRow key={it.id} item={it} canManage={canManage} onEdit={() => onEditItem(it)}
              onReport={() => onReport({ itemId: it.id, label: it.name })}
              reorderMode={reorderMode}
              itemIndex={idx} itemsCount={arr.length}
              onMove={(delta) => onMoveItem?.(comp.id, idx, delta)} />
          ))}
          {children.map((c, idx, arr) => (
            <CompartmentNode key={c.id} comp={c} byParent={byParent} depth={depth + 1}
              reorderMode={reorderMode}
              canManage={canManage}
              siblingIndex={idx} siblingsCount={arr.length}
              onMoveComp={(delta) => onMoveSub(arr, idx, delta)}
              onMoveItem={onMoveItem}
              onAddSub={onAddSub} onAddItem={onAddItem}
              onEditComp={onEditComp} onEditItem={onEditItem} onReport={onReport} />
          ))}
        </div>
      )}
    </div>
  );
}

function iconBtn(bg: string, color = 'white'): React.CSSProperties {
  return {
    width: 32, height: 32, padding: 0, border: 'none', borderRadius: 6, cursor: 'pointer',
    background: bg, color, fontSize: 13,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}

function ItemRow({ item, canManage, onEdit, onReport, reorderMode, itemIndex, itemsCount, onMove }: {
  item: CompartmentItem; canManage: boolean; onEdit: () => void; onReport: () => void;
  reorderMode?: boolean; itemIndex?: number; itemsCount?: number; onMove?: (delta: number) => void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const reportCounts = useReportCounts();
  const itemReports = reportCounts.item.get(item.id) ?? 0;

  async function handleDelete(): Promise<void> {
    const ok = await confirm({
      title: 'Elimina oggetto',
      message: `Eliminare "${item.name}"?`,
      confirmLabel: '🗑️ Elimina',
      variant: 'danger',
      icon: '🗑️',
    });
    if (!ok) return;
    try {
      await api.delete(`/vehicles/items/${item.id}`);
      qc.invalidateQueries({ queryKey: ['vehicle.compartments'] });
      toast.show('Oggetto eliminato', 'success');
    } catch {
      toast.show('Errore eliminazione', 'error');
    }
  }

  return (
    // Bryan: 'flex-direction: column-reverse; flex-wrap: wrap; align-items: flex-start'.
    // column-reverse: con actions in fondo al markup ma in alto visivamente,
    // il nome oggetto (sotto nel JSX) appare in alto e i pulsanti si pongono
    // visivamente sopra. wrap permette layout fluido su molteplici line.
    <div className="item-card" style={{
      display: 'flex', flexDirection: 'column-reverse', flexWrap: 'wrap',
      alignItems: 'flex-start', padding: '8px 12px', marginTop: 6,
      background: 'white', border: '1px solid #e9ecef', borderRadius: 6, gap: 6,
    }}>
      <div className="item-card-actions-row">
        {reorderMode && onMove && typeof itemIndex === 'number' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button onClick={() => onMove(-1)} disabled={itemIndex === 0}
              title="Sposta su"
              style={{ background: '#17a2b8', color: 'white', border: 'none', borderRadius: 3,
                       width: 22, height: 16, cursor: itemIndex === 0 ? 'not-allowed' : 'pointer',
                       fontSize: 9, opacity: itemIndex === 0 ? 0.4 : 1 }}>▲</button>
            <button onClick={() => onMove(1)} disabled={itemIndex === (itemsCount ?? 0) - 1}
              title="Sposta giu"
              style={{ background: '#17a2b8', color: 'white', border: 'none', borderRadius: 3,
                       width: 22, height: 16, cursor: itemIndex === (itemsCount ?? 0) - 1 ? 'not-allowed' : 'pointer',
                       fontSize: 9, opacity: itemIndex === (itemsCount ?? 0) - 1 ? 0.4 : 1 }}>▼</button>
          </div>
        )}
        {canManage && (
          <button onClick={onEdit} title="Modifica" style={iconBtn('#007bff')}>✎</button>
        )}
        <button onClick={onReport} title="Segnalazione" style={{ ...iconBtn('#ffc107', '#000'), position: 'relative' }}>
          🚨{itemReports > 0 && <span className="report-counter-badge">{itemReports}</span>}
        </button>
        {canManage && (
          <button onClick={handleDelete} title="Elimina" style={iconBtn('#dc3545')}>🗑️</button>
        )}
      </div>
      <span className="item-card-name" style={{ fontSize: 14 }}>
        🔧 <strong>{item.name}</strong>
        <span style={{ color: '#666', marginLeft: 8, fontSize: 13 }}>
          ({item.quantity} {item.unit || 'pz'})
        </span>
        {item.serialNumber && <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>#{item.serialNumber}</span>}
        {item.expiry && <span style={{ color: expiryColor(item.expiry), marginLeft: 8, fontSize: 12 }}>📅 {item.expiry}</span>}
      </span>
    </div>
  );
}

function VehicleFormModal({ vehicle, onClose }: { vehicle?: Vehicle; onClose: () => void }): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(vehicle?.name ?? '');
  const [plate, setPlate] = useState(vehicle?.plate ?? '');
  const [inspectionDate, setInspectionDate] = useState(vehicle?.inspectionDate?.slice(0, 10) ?? '');
  const [insuranceExpiry, setInsuranceExpiry] = useState(vehicle?.insuranceExpiry?.slice(0, 10) ?? '');
  const [lastMaintenance, setLastMaintenance] = useState(vehicle?.lastMaintenance?.slice(0, 10) ?? '');
  const [maintenanceNotes, setMaintenanceNotes] = useState(vehicle?.maintenanceNotes ?? '');

  async function save(): Promise<void> {
    try {
      const payload = {
        name, plate: plate || null,
        inspectionDate: inspectionDate || null,
        insuranceExpiry: insuranceExpiry || null,
        lastMaintenance: lastMaintenance || null,
        maintenanceNotes: maintenanceNotes || null,
      };
      if (vehicle) {
        await api.put(`/vehicles/${vehicle.id}`, payload);
      } else {
        await api.post('/vehicles', payload);
      }
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.invalidateQueries({ queryKey: ['vehicle', vehicle?.id] });
      toast.show(vehicle ? 'Mezzo aggiornato' : 'Mezzo creato', 'success');
      onClose();
    } catch {
      toast.show('Errore salvataggio', 'error');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3>{vehicle ? '✏️ Modifica Mezzo' : '+ Aggiungi Mezzo'}</h3>
        <div className="form-group">
          <label>Nome Mezzo *</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="Es. APS 001, ABP 002, Autobotte..." autoFocus />
        </div>
        <div className="form-group">
          <label>Targa</label>
          <input value={plate} onChange={e => setPlate(e.target.value)}
            placeholder="Es. AB123CD" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Scadenza Tagliando</label>
            <input type="date" value={inspectionDate} onChange={e => setInspectionDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Scadenza Assicurazione</label>
            <input type="date" value={insuranceExpiry} onChange={e => setInsuranceExpiry(e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label>Ultima Manutenzione</label>
          <input type="date" value={lastMaintenance} onChange={e => setLastMaintenance(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Note Manutenzione</label>
          <textarea rows={3} value={maintenanceNotes} onChange={e => setMaintenanceNotes(e.target.value)}
            placeholder="Storico manutenzioni, interventi effettuati..." />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} className="btn-secondary"
            style={{ padding: '10px 20px', fontWeight: 600 }}>Annulla</button>
          <button onClick={save} disabled={!name}
            style={{ padding: '10px 22px', fontWeight: 700, border: 'none', borderRadius: 8, cursor: name ? 'pointer' : 'not-allowed',
                     background: name ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)' : '#aaa', color: 'white',
                     boxShadow: '0 2px 6px rgba(40, 167, 69, 0.3)' }}>
            💾 Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function CompartmentFormModal({ vehicleId, parentCompartmentId, compartment, onClose }: {
  vehicleId: string; parentCompartmentId?: string | null;
  compartment?: Compartment; onClose: () => void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(compartment?.name ?? '');
  const editing = !!compartment;
  const isSub = !!(compartment?.parentCompartmentId ?? parentCompartmentId);

  async function save(): Promise<void> {
    try {
      if (editing) {
        await api.put(`/vehicles/compartments/${compartment!.id}`, { name });
      } else {
        await api.post('/vehicles/compartments', {
          vehicleId, parentCompartmentId: parentCompartmentId ?? null, name,
        });
      }
      qc.invalidateQueries({ queryKey: ['vehicle.compartments', vehicleId] });
      toast.show(editing ? 'Aggiornato' : 'Creato', 'success');
      onClose();
    } catch {
      toast.show('Errore salvataggio', 'error');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h3>{editing ? '✏️ Modifica' : '+ Aggiungi'} {isSub ? 'Sotto-vano/Cassetta' : 'Vano/Cassetta'}</h3>
        <div className="form-group">
          <label>Nome *</label>
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="Es. Vano superiore destro, Cassetta attrezzi..." autoFocus />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} className="btn-secondary" style={{ padding: '10px 20px', fontWeight: 600 }}>Annulla</button>
          <button onClick={save} disabled={!name}
            style={{ padding: '10px 22px', fontWeight: 700, border: 'none', borderRadius: 8, cursor: name ? 'pointer' : 'not-allowed',
                     background: name ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)' : '#aaa', color: 'white',
                     boxShadow: '0 2px 6px rgba(40, 167, 69, 0.3)' }}>
            💾 Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemFormModal({ compartmentId, item, onClose }: {
  compartmentId: string; item?: CompartmentItem; onClose: () => void;
}): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(item?.name ?? '');
  const [quantity, setQuantity] = useState(item?.quantity ?? 1);
  const [unit, setUnit] = useState(item?.unit ?? '');
  const [serialNumber, setSerialNumber] = useState(item?.serialNumber ?? '');
  const [expiry, setExpiry] = useState(item?.expiry?.slice(0, 10) ?? '');
  const editing = !!item;

  async function save(): Promise<void> {
    try {
      const payload = {
        compartmentId, name, quantity,
        unit: unit || null,
        serialNumber: serialNumber || null,
        expiry: expiry || null,
      };
      if (editing) {
        await api.put(`/vehicles/items/${item!.id}`, payload);
      } else {
        await api.post('/vehicles/items', payload);
      }
      qc.invalidateQueries({ queryKey: ['vehicle.compartments'] });
      toast.show(editing ? 'Aggiornato' : 'Aggiunto', 'success');
      onClose();
    } catch {
      toast.show('Errore salvataggio', 'error');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h3>{editing ? '✏️ Modifica' : '+ Aggiungi'} Oggetto</h3>
        <div className="form-group">
          <label>Nome *</label>
          <input value={name} onChange={e => setName(e.target.value)} autoFocus />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Quantità *</label>
            <input type="number" value={quantity} onChange={e => setQuantity(parseFloat(e.target.value))} step="0.01" min="0" />
          </div>
          <div className="form-group">
            <label>Unità</label>
            <input value={unit} onChange={e => setUnit(e.target.value)} placeholder="pz, lt, kg..." />
          </div>
        </div>
        <div className="form-group">
          <label>Matricola/Seriale</label>
          <input value={serialNumber} onChange={e => setSerialNumber(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Scadenza</label>
          <input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} className="btn-secondary" style={{ padding: '10px 20px', fontWeight: 600 }}>Annulla</button>
          <button onClick={save} disabled={!name}
            style={{ padding: '10px 22px', fontWeight: 700, border: 'none', borderRadius: 8, cursor: name ? 'pointer' : 'not-allowed',
                     background: name ? 'linear-gradient(135deg, #28a745 0%, #218838 100%)' : '#aaa', color: 'white',
                     boxShadow: '0 2px 6px rgba(40, 167, 69, 0.3)' }}>
            💾 Salva
          </button>
        </div>
      </div>
    </div>
  );
}

